import { useEffect, useRef } from "react";
import { LAND } from "./worldLand";
import { REGION_COUNTRIES } from "./regionCountries";
import { bindAnimPause } from "./animPause";

/*
 * World route field — flat aeronautical chart: coarse coastlines, a graticule,
 * and great-circle tracks between real hub coordinates. The density IS the
 * argument: this is the size of the surface a fleet decision system searches.
 * Land, grid and routes render once to an offscreen canvas; only the traffic
 * moves per frame, so the cost is ~100 dots regardless of route count.
 */

type Pt = [number, number];

interface Hub {
  lon: number;
  lat: number;
  code: string;
  tier: number;
  sx: number;
  sy: number;
}

/* hubs: lon, lat, code, tier */
const HUB_DATA: [number, number, string, number][] = [
  [-0.45, 51.47, "LHR", 1], [2.55, 49.01, "CDG", 1], [4.76, 52.31, "AMS", 1], [8.56, 50.03, "FRA", 1],
  [28.75, 41.28, "IST", 1], [55.36, 25.25, "DXB", 1], [51.61, 25.27, "DOH", 1], [103.99, 1.36, "SIN", 1],
  [113.91, 22.31, "HKG", 1], [121.81, 31.14, "PVG", 1], [116.58, 40.08, "PEK", 1], [126.44, 37.46, "ICN", 1],
  [140.39, 35.76, "NRT", 1], [-73.78, 40.64, "JFK", 1], [-118.41, 33.94, "LAX", 1], [-87.9, 41.98, "ORD", 1],
  [-84.43, 33.64, "ATL", 1], [-97.04, 32.9, "DFW", 1], [-122.38, 37.62, "SFO", 1], [-79.63, 43.68, "YYZ", 1],
  [-46.47, -23.43, "GRU", 1], [-99.07, 19.44, "MEX", 1], [-3.56, 40.47, "MAD", 1], [28.24, -26.13, "JNB", 1],
  [72.87, 19.09, "BOM", 1], [77.1, 28.56, "DEL", 1], [100.75, 13.69, "BKK", 1], [151.18, -33.94, "SYD", 1],
  [19.26, 47.44, "BUD", 2], [12.25, 41.8, "FCO", 2], [16.57, 48.12, "VIE", 2], [8.55, 47.46, "ZRH", 2], [-9.14, 38.77, "LIS", 2],
  [37.41, 55.97, "SVO", 2], [30.45, 50.34, "KBP", 2], [-58.54, -34.82, "EZE", 2], [-70.79, -33.39, "SCL", 2],
  [-77.11, -12.02, "LIM", 2], [-74.15, 4.7, "BOG", 2], [-43.25, -22.81, "GIG", 2], [18.6, -33.97, "CPT", 2],
  [3.32, 6.58, "LOS", 2], [31.41, 30.11, "CAI", 2], [36.93, -1.32, "NBO", 2], [38.8, 8.98, "ADD", 2],
  [-7.59, 33.37, "CMN", 2], [34.89, 32.01, "TLV", 2], [46.7, 24.96, "RUH", 2], [101.71, 2.74, "KUL", 2],
  [106.66, -6.13, "CGK", 2], [121.02, 14.51, "MNL", 2], [105.81, 21.22, "HAN", 2], [114.06, 22.64, "SZX", 2],
  [121.23, 25.08, "TPE", 2], [139.78, 35.55, "HND", 2], [144.84, -37.67, "MEL", 2], [153.12, -27.38, "BNE", 2],
  [115.97, -31.94, "PER", 2], [174.79, -37.01, "AKL", 2], [-149.99, 61.17, "ANC", 2], [-22.62, 63.99, "KEF", 2],
  [-6.27, 53.42, "DUB", 2], [-2.28, 53.36, "MAN", 2], [13.29, 52.56, "BER", 2], [11.79, 48.35, "MUC", 2],
  [2.08, 41.3, "BCN", 2], [23.94, 37.94, "ATH", 2], [24.97, 60.32, "HEL", 2], [17.92, 59.65, "ARN", 2],
  [-104.67, 39.86, "DEN", 2], [-80.29, 25.79, "MIA", 2], [-122.31, 47.45, "SEA", 2], [-71.01, 42.36, "BOS", 2],
  [-77.46, 38.94, "IAD", 2], [-95.34, 29.98, "IAH", 2], [-115.15, 36.08, "LAS", 2], [-123.18, 49.19, "YVR", 2],
  [-73.75, 45.47, "YUL", 2],
];

interface Route {
  a: number;
  b: number;
  long: boolean;
  ll: Pt[];
  px: Pt[];
}

interface Leg {
  r: Route;
  fwd: boolean;
}

interface Aircraft {
  rot: Leg[];
  leg: number;
  t: number;
  sp: number;
  hold: number;
}

export default function NetworkChartArt() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const g = c.getContext("2d");
    if (!g) return;

    const CS = getComputedStyle(document.documentElement);
    const C = (n: string) => CS.getPropertyValue(n).trim();
    const CA = (n: string, a: number) => {
      let h = C(n).replace("#", "");
      if (h.length === 3) h = h.split("").map((x) => x + x).join("");
      const v = parseInt(h, 16);
      return `rgba(${(v >> 16) & 255},${(v >> 8) & 255},${v & 255},${a})`;
    };
    /* site palette: routes fly the cyan accent, hubs sit in amber */
    const T = { land: "--line-3", grid: "--line-2", route: "--cyan", hub: "--amber" };
    const RM = matchMedia("(prefers-reduced-motion: reduce)").matches;

    const HUBS: Hub[] = HUB_DATA.map(([lon, lat, code, tier]) => ({ lon, lat, code, tier, sx: 0, sy: 0 }));

    /* projection: equirectangular, cover-fitted. Desktop shows the whole
       world; phones zoom onto Europe (Maghreb coast to the North Cape) */
    const LON0 = 10;
    let latN = 78, LATC = 11;
    const R = Math.PI / 180;
    let W = 0, H = 0, D = 1, S = 1, ox = 0, oy = 0, mx = -9999, my = -9999;
    /* effective southern limit: the phone view zooms into the Europe/Africa
       lon window, where no land sits below Cape Town — crop the ocean under it */
    let latS = -56;
    const wrap = (l: number) => ((l - LON0 + 540) % 360) - 180;
    const px = (lon: number, lat: number): Pt => [ox + wrap(lon) * S, oy - (lat - LATC) * S];

    /* great circles, sampled in lon/lat once and projected on resize */
    const v3 = (lon: number, lat: number) => {
      const a = lon * R, b = lat * R, cb = Math.cos(b);
      return [cb * Math.cos(a), cb * Math.sin(a), Math.sin(b)];
    };
    function gc(A: Hub, B: Hub, n: number): Pt[] {
      const a = v3(A.lon, A.lat), b = v3(B.lon, B.lat);
      const dot = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
      const d = Math.acos(dot), out: Pt[] = [];
      if (d < 1e-6) return [[A.lon, A.lat], [B.lon, B.lat]];
      for (let i = 0; i <= n; i++) {
        const t = i / n, s1 = Math.sin((1 - t) * d) / Math.sin(d), s2 = Math.sin(t * d) / Math.sin(d);
        const v = [a[0] * s1 + b[0] * s2, a[1] * s1 + b[1] * s2, a[2] * s1 + b[2] * s2];
        out.push([Math.atan2(v[1], v[0]) / R, Math.asin(Math.max(-1, Math.min(1, v[2]))) / R]);
      }
      return out;
    }
    const ang = (i: number, j: number) => {
      const a = v3(HUBS[i].lon, HUBS[i].lat), b = v3(HUBS[j].lon, HUBS[j].lat);
      return Math.acos(Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2])));
    };

    /* network shape: hub-and-spoke, the way airlines actually fly.
       Spokes hang off their nearest hubs. Hubs interconnect on trunks. Nothing
       here is a random graph: every line is a sector somebody could roster. */
    const HUB: number[] = [], SPOKE: number[] = [];
    HUBS.forEach((h, i) => (h.tier === 1 ? HUB : SPOKE).push(i));
    const ROUTES: Route[] = [], idx = new Map<string, number>();
    function route(i: number, j: number): Route {
      const k = i < j ? i + ":" + j : j + ":" + i;
      const hit = idx.get(k);
      if (hit !== undefined) return ROUTES[hit];
      const far = ang(i, j);
      const r: Route = { a: i, b: j, long: far > 0.55, ll: gc(HUBS[i], HUBS[j], far > 0.35 ? 56 : 20), px: [] };
      idx.set(k, ROUTES.length);
      ROUTES.push(r);
      return r;
    }
    const spokesOf = new Map<number, number[]>();
    SPOKE.forEach((s) => {
      const near = HUB.slice().sort((p, q) => ang(s, p) - ang(s, q));
      const n = ang(s, near[1]) < 0.55 ? 2 : 1; /* well-placed spokes feed two hubs */
      for (let k = 0; k < n; k++) {
        route(s, near[k]);
        if (!spokesOf.has(near[k])) spokesOf.set(near[k], []);
        spokesOf.get(near[k])!.push(s);
      }
    });
    const TRUNK: Route[] = [];
    HUB.forEach((i, n) => {
      const o = HUB.filter((j) => j !== i).sort((p, q) => ang(i, p) - ang(i, q));
      [0, 1, 2, o.length - 1, o.length - 2, o.length - 4, (n * 5) % o.length].forEach((k) => {
        const j = o[Math.max(0, Math.min(o.length - 1, k))];
        if (j !== undefined && j !== i) TRUNK.push(route(i, j));
      });
    });

    /* rotations: an aircraft flies a sequence of legs, not one line.
       out-and-back  H→S→H · W pattern  H→A→B→A→H · trunk shuttle  H1→H2→H1 */
    const ROT: Leg[][] = [];
    function legs(seq: number[]): Leg[] {
      const out: Leg[] = [];
      for (let i = 0; i < seq.length - 1; i++) {
        const a = seq[i], b = seq[i + 1], r = route(a, b);
        out.push({ r, fwd: r.a === a });
      }
      return out;
    }
    HUB.forEach((h) => {
      const sp = spokesOf.get(h) || [];
      sp.forEach((s, k) => {
        const s2 = sp[(k + 1) % sp.length];
        if (k % 3 === 2 && sp.length > 1 && s2 !== s) ROT.push(legs([h, s, s2, s, h]));
        else ROT.push(legs([h, s, h]));
      });
    });
    TRUNK.forEach((r) => ROT.push(legs([r.a, r.b, r.a])));

    /* static layer */
    const off = document.createElement("canvas");
    const og = off.getContext("2d")!;
    const seam = (a: Pt, b: Pt) => Math.abs(a[0] - b[0]) > W * 0.55;
    function poly(ctx: CanvasRenderingContext2D, pts: Pt[]) {
      ctx.beginPath();
      let on = false;
      for (let i = 0; i < pts.length; i++) {
        if (i > 0 && seam(pts[i], pts[i - 1])) on = false;
        if (!on) { ctx.moveTo(pts[i][0], pts[i][1]); on = true; } else ctx.lineTo(pts[i][0], pts[i][1]);
      }
      ctx.stroke();
    }
    function project() {
      ROUTES.forEach((r) => { r.px = r.ll.map((p) => px(p[0], p[1])); });
      HUBS.forEach((h) => { const p = px(h.lon, h.lat); h.sx = p[0]; h.sy = p[1]; });
    }
    function statics() {
      off.width = W * D;
      off.height = H * D;
      og.setTransform(D, 0, 0, D, 0, 0);
      og.clearRect(0, 0, W, H);

      /* finer line work on the zoomed mobile view — applies to every stroke */
      const m = W < 720 ? 0.45 : 1;

      /* graticule — 30° meridians, 15° parallels, clamped to the map band */
      const yTop = Math.max(0, oy - (latN - LATC) * S);
      /* phone: the band is top-anchored, so let the graticule run to the
         bottom edge rather than stopping at the continents' southern limit */
      const yBot = W < 720 ? H : Math.min(H, oy - (latS - LATC) * S);
      og.strokeStyle = CA(T.grid, 0.75);
      og.lineWidth = 1 * m;
      const lonStep = m < 1 ? 15 : 30;
      for (let lon = -180; lon <= 180; lon += lonStep) {
        const x = ox + wrap(lon) * S;
        if (x < -20 || x > W + 20) continue;
        og.beginPath(); og.moveTo(x, yTop); og.lineTo(x, yBot); og.stroke();
      }
      const latStep = m < 1 ? 10 : 15;
      for (let lat = -45; lat <= 75; lat += latStep) {
        const y = oy - (lat - LATC) * S;
        if (y < -20 || y > H + 20) continue;
        og.beginPath(); og.moveTo(0, y); og.lineTo(W, y); og.stroke();
        if (lat === 0) {
          og.strokeStyle = CA(T.grid, 1); og.lineWidth = 1.6 * m;
          og.beginPath(); og.moveTo(0, y); og.lineTo(W, y); og.stroke();
          og.strokeStyle = CA(T.grid, 0.75); og.lineWidth = 1 * m;
        }
      }

      /* coastlines */
      /* desktop world view keeps the faint land fill (its closure chords
         are invisible at that scale); the phone's Europe zoom is
         outline-only — zoomed in, the chords cut visible facets */
      og.strokeStyle = CA(T.land, 0.62);
      og.lineWidth = 1.2 * m;
      og.fillStyle = CA(T.land, 0.09);
      LAND.forEach((p) => {
        const pts = p.map((q) => px(q[0], q[1]));
        og.beginPath();
        let on = false;
        for (let i = 0; i < pts.length; i++) {
          if (i > 0 && seam(pts[i], pts[i - 1])) on = false;
          if (!on) { og.moveTo(pts[i][0], pts[i][1]); on = true; } else og.lineTo(pts[i][0], pts[i][1]);
        }
        if (m === 1) og.fill();
        og.stroke();
      });

      /* country borders give the phone's zoomed-in view its detail; on
         the world view they would be regional clutter */
      if (m < 1) {
      og.strokeStyle = CA(T.land, 0.42);
      og.lineWidth = 0.8 * m;
      REGION_COUNTRIES.forEach((s) =>
        s.r.forEach((ring) => {
          const pts = ring.map((q) => px(q[0], q[1]));
          pts.push(pts[0]);
          poly(og, pts);
        })
      );
      }

      /* routes — long sectors read brighter, short-haul recedes into a web */
      ROUTES.forEach((r) => {
        og.strokeStyle = CA(T.route, r.long ? 0.24 : 0.12);
        og.lineWidth = (r.long ? 1 : 0.8) * m;
        poly(og, r.px);
      });

      /* hubs */
      HUBS.forEach((h) => {
        og.fillStyle = h.tier === 1 ? CA(T.hub, 1) : CA(T.hub, 0.55);
        og.beginPath();
        og.arc(h.sx, h.sy, h.tier === 1 ? 2.4 : 1.5, 0, 7);
        og.fill();
      });
    }

    /* traffic */
    const AC: Aircraft[] = [];
    ROT.forEach((rot, n) => {
      if (rot.length === 2 && n % 3 === 0) return; /* thin the simplest out-and-backs */
      if (window.innerWidth < 720 && n % 2) return; /* and half the fleet again on a phone */
      AC.push({ rot, leg: n % rot.length, t: ((n * 13) % 100) / 100, sp: 0.0015 + (n % 7) * 0.0004, hold: (n * 11) % 40 });
    });
    function at(pts: Pt[], t: number): Pt | null {
      const f = Math.max(0, Math.min(1, t)) * (pts.length - 1), i = Math.floor(f), u = f - i;
      const a = pts[i], b = pts[Math.min(pts.length - 1, i + 1)];
      if (seam(a, b)) return null;
      return [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u];
    }

    function size() {
      const r = c!.getBoundingClientRect();
      W = Math.max(1, r.width);
      H = Math.max(1, r.height);
      /* phones render at full device pixel ratio — the zoomed-in view makes
         any upscale read as blur; the small canvas keeps it cheap anyway */
      D = Math.min(devicePixelRatio || 1, W < 720 ? 3 : 2);
      c!.width = W * D;
      c!.height = H * D;
      g!.setTransform(D, 0, 0, D, 0, 0);
      /* scale from width alone: the whole longitude range must always be on
         screen, or a tall window silently crops the Americas out of the network.
         On a phone the whole planet across 390px is unreadable, so it zooms into
         a region instead — same map, closer in. */
      const phoneView = W < 720;
      latN = phoneView ? 72 : 78;
      latS = phoneView ? 24 : -56;
      LATC = (latN + latS) / 2;
      /* cover-fit: the continents band always fills the full height so the map
         stays behind the hero copy — a tall window crops longitudes at the
         edges instead of letterboxing empty ocean above or below the land.
         Phones back the zoom off ~20% to show more of the network; the small
         letterbox that opens at the top sits behind the fixed header. */
      S = Math.max(W / 360, (phoneView ? 0.8 : 1) * (H / (latN - latS)));
      ox = W / 2;
      /* desktop: southern edge of the continents pinned to the canvas bottom,
         any overflow crops the Arctic at the top. Phone: the zoomed-out map is
         pinned to the top instead so it starts at the top of the page. */
      oy = phoneView ? (latN - LATC) * S : H - (LATC - latS) * S;
      project();
      statics();
      /* setting c.width above wipes the visible canvas; with motion reduced there
         is no loop to put it back, so repaint here or a relayout blanks the map */
      if (RM) { g!.clearRect(0, 0, W, H); g!.drawImage(off, 0, 0, W, H); }
    }

    let tick = 0, rafLoop = 0, rafSize = 0;
    function frame() {
      tick++;
      g!.clearRect(0, 0, W, H);
      g!.drawImage(off, 0, 0, W, H);

      AC.forEach((a) => {
        if (a.hold > 0) a.hold--; /* turnaround on stand */
        else {
          a.t += a.sp;
          if (a.t >= 1) { a.t = 0; a.leg = (a.leg + 1) % a.rot.length; a.hold = 20 + ((a.leg * 7) % 30); }
        }
        const L = a.rot[a.leg];
        /* as a background the traffic stays dim so the copy in front reads */
        const dim = W < 720 ? 0.38 : 0.55;
        for (let k = 0; k < 9; k++) {
          const tt = a.t - k * 0.01;
          if (tt < 0 || tt > 1) continue;
          const p = at(L.r.px, L.fwd ? tt : 1 - tt);
          if (!p) continue;
          g!.fillStyle = CA(T.route, (1 - k / 9) * dim);
          g!.beginPath();
          g!.arc(p[0], p[1], 1.8 * (1 - k / 15), 0, 7);
          g!.fill();
        }
      });

      /* tier-1 hubs breathe so the eye finds the spine of the network */
      HUBS.forEach((h, i) => {
        if (h.tier !== 1) return;
        const p = 0.5 + 0.5 * Math.sin(tick / 52 + i);
        g!.strokeStyle = CA(T.hub, 0.26 * (1 - p));
        g!.lineWidth = 1;
        g!.beginPath();
        g!.arc(h.sx, h.sy, 2.4 + p * 13, 0, 7);
        g!.stroke();
      });

      /* nearest hub to the pointer names itself */
      if (mx > -9000) {
        let best: Hub | null = null, bd = 1e9;
        HUBS.forEach((h) => {
          const d = Math.hypot(mx - h.sx, my - h.sy);
          if (d < bd) { bd = d; best = h; }
        });
        if (best && bd < 90) {
          const b: Hub = best;
          g!.strokeStyle = CA(T.route, 0.85);
          g!.lineWidth = 1;
          g!.beginPath(); g!.arc(b.sx, b.sy, 9, 0, 7); g!.stroke();
          g!.beginPath();
          g!.moveTo(b.sx - 16, b.sy); g!.lineTo(b.sx - 11, b.sy);
          g!.moveTo(b.sx + 11, b.sy); g!.lineTo(b.sx + 16, b.sy);
          g!.moveTo(b.sx, b.sy - 16); g!.lineTo(b.sx, b.sy - 11);
          g!.moveTo(b.sx, b.sy + 11); g!.lineTo(b.sx, b.sy + 16);
          g!.stroke();
          g!.fillStyle = CA(T.route, 0.95);
          g!.font = "500 10px ui-monospace,SFMono-Regular,monospace";
          g!.textAlign = "center";
          g!.fillText(b.code, b.sx, b.sy - 20);
        }
      }
      if (!RM) rafLoop = requestAnimationFrame(frame);
    }

    /* two separate handles: the resize debounce must not cancel the render loop,
       or the first relayout after webfonts land kills the animation for good */
    /* phone URL-bar show/hide fires height-only resizes; the figure box
       is width-driven there, and a rebuild blanks the canvas for a frame */
    let lastArtW = window.innerWidth;
    const phoneMQ = window.matchMedia("(max-width: 768px)");
    const onResize = () => {
      if (phoneMQ.matches && window.innerWidth === lastArtW) return;
      lastArtW = window.innerWidth;
      cancelAnimationFrame(rafSize);
      rafSize = requestAnimationFrame(() => {
        size();
        if (!RM) { cancelAnimationFrame(rafLoop); rafLoop = requestAnimationFrame(frame); }
      });
    };
    const onMove = (e: PointerEvent) => {
      const r = c!.getBoundingClientRect();
      mx = e.clientX - r.left;
      my = e.clientY - r.top;
    };
    const onLeave = () => { mx = my = -9999; };
    window.addEventListener("resize", onResize);
    c.addEventListener("pointermove", onMove);
    c.addEventListener("pointerleave", onLeave);

    size();
    if (RM) g.drawImage(off, 0, 0, W, H);
    else frame();
    /* first frame is on the canvas — ease it in instead of popping */
    requestAnimationFrame(() => c.classList.add("ready"));
    const unbind = RM ? null : bindAnimPause(
      c,
      () => { cancelAnimationFrame(rafLoop); rafLoop = requestAnimationFrame(frame); },
      () => cancelAnimationFrame(rafLoop)
    );

    return () => {
      if (unbind) unbind();
      cancelAnimationFrame(rafLoop);
      cancelAnimationFrame(rafSize);
      window.removeEventListener("resize", onResize);
      c.removeEventListener("pointermove", onMove);
      c.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return <canvas ref={ref} className="hero-net" aria-hidden="true" />;
}
